import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Container,
  Icon,
  List,
  ListItem,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Step,
} from "semantic-ui-react";
import BarcodeInput from "../BarcodeInput/BarcodeInput";

import styles from "./SwapBoxModal.module.css";
import useInput from "../../../hooks/useInput";
import SelectBoxSize from "../SelectBoxSize/SelectBoxSize";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onTriggerBoxSwap: (
    currentBoxBarcode: string,
    newBoxBarcode: string,
    selectedBoxSize: string
  ) => void;
  currentBoxBarcode: string;
  isBoxSwapFinished: boolean;
  isPendingBoxSwap: boolean;
  isClosable?: boolean;
};

const SwapBoxModal = ({
  isOpen,
  onClose,
  onTriggerBoxSwap,
  currentBoxBarcode,
  isBoxSwapFinished,
  isPendingBoxSwap,
  isClosable = true,
}: Props) => {
  const [prevIsOpen, setPrevIsOpen] = useState<boolean>(false);
  // input refs
  const currentBoxInputRef = useRef<HTMLInputElement>(null);
  const newBoxInputRef = useRef<HTMLInputElement>(null);

  // state
  const [currentBarcode, setCurrentBarcode] = useState("");
  const [newBarcode, setNewBarcode] = useState("");
  const [selectedBoxSize, setSelectedBoxSize] = useState("");

  const isMatchingCurrentBoxBarcode = useCallback(
    (scannedBarcode: string) => scannedBarcode === currentBoxBarcode,
    [currentBoxBarcode]
  );

  const focusNextBarcodeInput = useCallback(() => newBoxInputRef.current?.focus(), []);

  const onScanCurrentBarcode = (value: string) => {
    setCurrentBarcode(value);
    focusNextBarcodeInput();
  };

  const hasSelectedBoxSize = (): boolean => {
    return selectedBoxSize !== "";
  };

  const onScanNewBarcode = (value: string) => setNewBarcode(value);

  useEffect(() => {
    if (!currentBarcode || !newBarcode || !hasSelectedBoxSize()) return;
    console.log(newBarcode);
    onTriggerBoxSwap(currentBarcode, newBarcode, selectedBoxSize);
  }, [newBarcode, selectedBoxSize]);

  useEffect(() => {
    if (prevIsOpen === isOpen) return;
    if (currentBoxInputRef.current) currentBoxInputRef.current.focus();
    setPrevIsOpen(isOpen);

    setSelectedBoxSize("");
  }, [isOpen, currentBoxInputRef]);

  return (
    <Modal dimmer="blurring" open={isOpen} size="large">
      <ModalHeader>Byt lådnummer för order</ModalHeader>
      <Step.Group attached="top" widths={3}>
        <Step
          disabled={isBoxSwapFinished}
          active={!isBoxSwapFinished}
          icon="barcode"
          link
          onClick={() => {}}
          title="Ange lådnummer"
          description="Ange det nuvarande och nya lådnummret"
        />
        <Step
          disabled={!isBoxSwapFinished}
          active={isBoxSwapFinished}
          icon="info circle"
          link
          onClick={() => {}}
          title="Kassera låda"
          description="Vänligen flytta över artiklarna till den nya lådan och kassera den gamla"
        />
        <Step disabled={true} icon="info circle" link onClick={() => {}} title="Välj lådstorlek" />
      </Step.Group>
      <ModalContent>
        <div className={styles["modal-content__inner"]}>
          {!isBoxSwapFinished ? (
            <>
              <div className={styles["test"]}>
                <BarcodeInput
                  disabled={false}
                  placeholder="lådnummer"
                  icon="barcode"
                  iconPosition="left"
                  size="big"
                  ref={currentBoxInputRef}
                  onValidate={isMatchingCurrentBoxBarcode}
                  onBarcodeScan={onScanCurrentBarcode}
                />
              </div>
              <Icon name="arrow right" size="big" color="grey" />
              <div className={styles["test"]}>
                <BarcodeInput
                  disabled={false}
                  placeholder="nytt lådnummer"
                  icon="barcode"
                  iconPosition="left"
                  size="big"
                  ref={newBoxInputRef}
                  onValidate={(value) =>
                    value !== "" &&
                    isMatchingCurrentBoxBarcode(currentBarcode) &&
                    !isMatchingCurrentBoxBarcode(value)
                  }
                  onBarcodeScan={onScanNewBarcode}
                  loading={isPendingBoxSwap}
                />
              </div>
              <Icon name="arrow right" size="big" color="grey" />
              <div className={styles["test"]}>
                <SelectBoxSize
                  selectedBoxSize={selectedBoxSize}
                  setSelectedBoxSize={setSelectedBoxSize}
                />
              </div>
            </>
          ) : (
            <>
              <List>
                <ListItem>
                  <b>Flytta artiklarna</b>: Flytta över samtliga artiklar från den gamla lådan till
                  den nya lådan.
                </ListItem>
                <ListItem>
                  <b>Kontrollera noggrant</b>: Säkerställ att inga artiklar lämnas kvar i den gamla
                  lådan.
                </ListItem>
                <ListItem>
                  <b>Kassera lådan</b>: När artiklarna är överflyttade, kassera den gamla lådan
                  enligt gällande rutiner för avfallshantering.
                </ListItem>
              </List>
            </>
          )}
        </div>
      </ModalContent>
      <ModalActions>
        {!isBoxSwapFinished && isClosable && (
          <Button negative onClick={onClose}>
            Avbryt
          </Button>
        )}
        {isBoxSwapFinished && (
          <Button positive onClick={onClose}>
            Bekräfta
          </Button>
        )}

        {/* <Button positive onClick={() => {}}>
          Agree
        </Button> */}
      </ModalActions>
    </Modal>
  );
};

export default SwapBoxModal;
